import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { getStripeEnvironment } from '@/lib/stripe';

export type PaidModuleKey =
  | 'ai_assistant'
  | 'advanced_analytics'
  | 'inspections'
  | 'training';

export type BillingInterval = 'month' | 'year';

export interface PaidModule {
  key: PaidModuleKey;
  name: string;
  description: string;
  price_per_facility_cents: number;
  stripe_price_id: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface OrgEntitlement {
  id: string;
  organization_id: string;
  module_key: PaidModuleKey;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | string;
  stripe_subscription_id: string | null;
  stripe_subscription_item_id: string | null;
  stripe_customer_id: string | null;
  quantity: number;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  billing_interval: BillingInterval | string;
  environment: 'sandbox' | 'live' | string;
}

export interface BaseSubscription {
  id: string;
  organization_id: string;
  tier: 'standard' | 'professional';
  billing_interval: BillingInterval;
  status: string;
  quantity: number;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: 'sandbox' | 'live' | string;
  stripe_subscription_id: string | null;
}

const ACTIVE_STATUSES = new Set(['active', 'trialing']);

function safeEnv(): 'sandbox' | 'live' {
  try { return getStripeEnvironment(); } catch { return 'sandbox'; }
}

export function useEntitlements() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const env = safeEnv();

  const modulesQ = useQuery({
    queryKey: ['paid-modules'],
    queryFn: async (): Promise<PaidModule[]> => {
      const { data, error } = await supabase
        .from('paid_modules' as any)
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return (data || []) as unknown as PaidModule[];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const entitlementsQ = useQuery({
    queryKey: ['org-entitlements', orgId, env],
    queryFn: async (): Promise<OrgEntitlement[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('organization_entitlements' as any)
        .select('*')
        .eq('organization_id', orgId)
        .eq('environment', env);
      if (error) throw error;
      return (data || []) as unknown as OrgEntitlement[];
    },
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });

  const baseSubQ = useQuery({
    queryKey: ['base-subscription', orgId, env],
    queryFn: async (): Promise<BaseSubscription | null> => {
      if (!orgId) return null;
      const { data, error } = await supabase
        .from('base_subscriptions' as any)
        .select('*')
        .eq('organization_id', orgId)
        .eq('environment', env)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as BaseSubscription) || null;
    },
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });

  // Sandbox grandfather rows only count while the org is inside the 30-day
  // pilot grace window. After grace expires, sandbox-only orgs lose write
  // access; live entitlements always count.
  const pilotGraceUntilMs = organization?.pilot_grace_until
    ? new Date(organization.pilot_grace_until).getTime()
    : null;
  const inPilotGrace = pilotGraceUntilMs !== null && pilotGraceUntilMs > Date.now();
  const isPilotExempt = !!organization?.pilot_exempt;

  const hasModule = (key: PaidModuleKey): boolean => {
    // Pilot-exempt orgs (e.g. internal demo) always have access.
    if (isPilotExempt) return true;
    const row = entitlementsQ.data?.find((e) => e.module_key === key);
    if (!row) return false;
    // Sandbox row: only honor while pilot grace is active.
    if (row.environment === 'sandbox' && !inPilotGrace) return false;
    if (ACTIVE_STATUSES.has(row.status)) return true;
    if (row.status === 'past_due') return true;
    if (row.status === 'canceled' && row.current_period_end) {
      return new Date(row.current_period_end).getTime() > Date.now();
    }
    return false;
  };


  const getModule = (key: PaidModuleKey) =>
    modulesQ.data?.find((m) => m.key === key);

  const getEntitlement = (key: PaidModuleKey) =>
    entitlementsQ.data?.find((e) => e.module_key === key);

  const baseSubscription = baseSubQ.data || null;
  const isPastDue =
    !isPilotExempt && (
      baseSubscription?.status === 'past_due' ||
      (entitlementsQ.data || []).some((e) => e.status === 'past_due')
    );

  return {
    modules: modulesQ.data ?? [],
    entitlements: entitlementsQ.data ?? [],
    baseSubscription,
    hasModule,
    getModule,
    getEntitlement,
    isPastDue,
    inPilotGrace: isPilotExempt ? true : inPilotGrace,
    isPilotExempt,
    pilotGraceUntil: organization?.pilot_grace_until ?? null,
    pilotDaysRemaining:
      isPilotExempt
        ? null
        : pilotGraceUntilMs !== null
          ? Math.ceil((pilotGraceUntilMs - Date.now()) / (24 * 60 * 60 * 1000))
          : null,
    environment: env,
    isLoading:
      modulesQ.isLoading || entitlementsQ.isLoading || baseSubQ.isLoading,
  };
}

